import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Users, FileVideo, LogOut, KeyRound, History, Sparkles } from "lucide-react";

const linkBase = "px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 transition";
const active = "bg-white/10 text-white";
const idle = "text-slate-300 hover:bg-white/5 hover:text-white";

export default function AdminShell({ children, title, subtitle, right }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const onLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#0F1020] text-slate-100">
      <header className="border-b border-white/10 bg-[#141631]/70 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <Link to="/admin" data-testid="admin-brand" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-rose-400 flex items-center justify-center">
              <ShieldCheck size={18} className="text-[#0F1020]" />
            </div>
            <div>
              <div className="font-display font-bold text-white leading-none">Breathe·Thrive</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 mt-0.5">Admin Console</div>
            </div>
          </Link>
          <nav className="flex items-center gap-1">
            <NavLink to="/admin" end data-testid="admin-nav-dashboard" className={({ isActive }) => `${linkBase} ${isActive ? active : idle}`}>
              <ShieldCheck size={14} /> Overview
            </NavLink>
            <NavLink to="/admin/families" data-testid="admin-nav-families" className={({ isActive }) => `${linkBase} ${isActive ? active : idle}`}>
              <Users size={14} /> Families
            </NavLink>
            <NavLink to="/admin/activity" data-testid="admin-nav-activity" className={({ isActive }) => `${linkBase} ${isActive ? active : idle}`}>
              <History size={14} /> Activity
            </NavLink>
            <NavLink to="/admin/content" data-testid="admin-nav-content" className={({ isActive }) => `${linkBase} ${isActive ? active : idle}`}>
              <FileVideo size={14} /> Content
            </NavLink>
            <NavLink to="/admin/thrive-stories" data-testid="admin-nav-thrive-stories" className={({ isActive }) => `${linkBase} ${isActive ? active : idle}`}>
              <Sparkles size={14} /> Thrive Stories
            </NavLink>
          </nav>
          <div className="flex items-center gap-2 text-xs">
            <span className="hidden md:inline text-slate-400">{user?.email}</span>
            <Link to="/admin/security" data-testid="admin-security-link" className="px-3 py-2 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white inline-flex items-center gap-2">
              <KeyRound size={14} /> Password
            </Link>
            <button onClick={onLogout} data-testid="admin-logout" className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white inline-flex items-center gap-2">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>
      {(title || subtitle || right) && (
        <div className="max-w-[1400px] mx-auto px-6 pt-8 pb-4 flex items-end justify-between flex-wrap gap-4">
          <div>
            {subtitle && <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-semibold">{subtitle}</div>}
            {title && <h1 className="font-display text-3xl md:text-4xl font-bold text-white mt-1">{title}</h1>}
          </div>
          {right}
        </div>
      )}
      <main className="max-w-[1400px] mx-auto px-6 pb-16">{children}</main>
    </div>
  );
}
